import React, { useEffect, useState, useCallback, useRef } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { Bell, Warning, XCircle } from "@phosphor-icons/react";

export default function LowStockBell() {
  const { currentStoreId, stores } = useAuth();
  const [data, setData] = useState({ low: [], oos: [], low_count: 0, oos_count: 0 });
  const [open, setOpen] = useState(false);
  const popRef = useRef(null);
  const btnRef = useRef(null);

  const load = useCallback(async () => {
    if (!currentStoreId) return;
    try {
      const r = await api.get(`/store/${currentStoreId}/alerts`);
      setData(r.data);
    } catch (e) { /* silent */ }
  }, [currentStoreId]);

  useEffect(() => {
    load();
    const t = setInterval(load, 60_000);
    return () => clearInterval(t);
  }, [load]);

  useEffect(() => {
    if (!open) return;
    const onDocClick = (e) => {
      if (popRef.current && popRef.current.contains(e.target)) return;
      if (btnRef.current && btnRef.current.contains(e.target)) return;
      setOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [open]);

  if (!currentStoreId) return null;
  const total = (data.low_count || 0) + (data.oos_count || 0);
  const storeName = stores.find((s) => s.id === currentStoreId)?.name || "Store";

  return (
    <div className="relative">
      <button
        ref={btnRef}
        data-testid="lowstock-bell"
        onClick={() => setOpen((v) => !v)}
        className={`relative inline-flex items-center justify-center w-10 h-10 rounded-md border transition-colors ${
          total > 0 ? "border-amber-300 bg-amber-50 hover:bg-amber-100 text-amber-700" : "border-slate-200 bg-white hover:bg-slate-50 text-slate-600"
        }`}
        title={`${total} stock alerts at ${storeName}`}
      >
        <Bell size={18} weight={total > 0 ? "fill" : "regular"} />
        {total > 0 && (
          <span
            data-testid="lowstock-badge"
            className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 flex items-center justify-center text-[10px] font-bold text-white bg-red-600 rounded-full border-2 border-white"
          >
            {total > 99 ? "99+" : total}
          </span>
        )}
      </button>

      {open && (
        <div
          ref={popRef}
          data-testid="lowstock-popover"
          className="absolute right-0 top-12 w-[360px] max-h-[480px] bg-white border border-slate-200 rounded-md shadow-xl z-50 flex flex-col overflow-hidden"
        >
          <div className="px-4 py-3 border-b border-slate-200 bg-slate-50">
            <div className="font-display font-bold text-sm flex items-center gap-2">
              <Bell size={15} weight="fill" className="text-amber-600" />
              Stock Alerts · {storeName}
            </div>
            <div className="text-[11px] text-slate-500 mt-0.5 font-mono-num">
              {data.oos_count} out · {data.low_count} low · refreshes every 60s
            </div>
          </div>
          <div className="flex-1 overflow-y-auto">
            {total === 0 && (
              <div className="text-center py-10 text-sm text-slate-400">
                All good — no items at or below reorder level.
              </div>
            )}
            {data.oos.length > 0 && (
              <div>
                <div className="px-4 py-1.5 bg-red-50 text-red-700 text-[10px] uppercase tracking-widest font-bold border-b border-red-100 inline-flex items-center gap-1">
                  <XCircle size={11} weight="fill" />Out of Stock ({data.oos_count})
                </div>
                {data.oos.slice(0, 20).map((it) => (
                  <div key={"o-" + it.sku} className="px-4 py-2 border-b border-slate-100 flex items-center justify-between gap-2 hover:bg-slate-50">
                    <div className="min-w-0">
                      <div className="text-xs font-semibold truncate">{it.product_name}</div>
                      <div className="text-[10px] text-slate-500 font-mono-num">{it.sku}</div>
                    </div>
                    <div className="text-right shrink-0">
                      <div className="text-xs font-mono-num font-bold text-red-600">{it.qty}</div>
                      <div className="text-[9px] text-slate-400 uppercase tracking-wider">re-order {it.reorder_level}</div>
                    </div>
                  </div>
                ))}
              </div>
            )}
            {data.low.length > 0 && (
              <div>
                <div className="px-4 py-1.5 bg-amber-50 text-amber-700 text-[10px] uppercase tracking-widest font-bold border-b border-amber-100 inline-flex items-center gap-1">
                  <Warning size={11} weight="fill" />Low Stock ({data.low_count})
                </div>
                {data.low.slice(0, 30).map((it) => (
                  <div key={"l-" + it.sku} className="px-4 py-2 border-b border-slate-100 flex items-center justify-between gap-2 hover:bg-slate-50">
                    <div className="min-w-0">
                      <div className="text-xs font-semibold truncate">{it.product_name}</div>
                      <div className="text-[10px] text-slate-500 font-mono-num">{it.sku}</div>
                    </div>
                    <div className="text-right shrink-0">
                      <div className="text-xs font-mono-num font-bold text-amber-600">{it.qty}</div>
                      <div className="text-[9px] text-slate-400 uppercase tracking-wider">re-order {it.reorder_level}</div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
          <Link
            to={`/store/${currentStoreId}/inventory`}
            onClick={() => setOpen(false)}
            data-testid="lowstock-view-all"
            className="block text-center py-2.5 text-xs font-bold uppercase tracking-widest text-emerald-700 hover:bg-emerald-50 border-t border-slate-200"
          >
            View full inventory →
          </Link>
        </div>
      )}
    </div>
  );
}
